import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, FolderTree, Tag, Percent, Layers, DollarSign, Shield } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import GSTRateConfig from '../components/GSTRateConfig';
import BulkSubcategoryModal from '../components/BulkSubcategoryModal';
import CategoryCSVManager from '../components/CategoryCSVManager';

// iter-332 — Compliance & Regulatory Rules defaults (License / Shelf-Life /
// Warranty / Return-Window), all admin-configurable per category/subcategory.
const COMPLIANCE_DEFAULTS = {
  license_field_label: '',
  license_badge_url: '',
  enable_expiry_rules: false,
  expiry_date_format: 'shelf_life',
  expiry_legal_label: 'Best Before',
  min_guaranteed_shelf_life_days: '',
  min_guaranteed_shelf_life_percent: '',
  enable_warranty_rules: false,
  default_assurance_type: 'manufacturer_warranty',
  default_warranty_duration_unit: 'months',
  buying_mode_override_enabled: false,
  buying_mode_override_type: 'enquiry',
  deemed_eco_category: false,
};

// iter-347 — Scrub compliance payload before sending to the API: empty
// numeric strings become null (avoids a 422 from FastAPI's Optional[float]
// parsing), and both shelf-life floor fields are cleared together when
// Expiry Rules are toggled off (mirrors the backend's required-together rule).
const scrubCompliancePayload = (data) => {
  const clean = { ...data };
  ['min_guaranteed_shelf_life_days', 'min_guaranteed_shelf_life_percent'].forEach((k) => {
    if (clean[k] === '' || clean[k] === undefined) clean[k] = null;
    else if (typeof clean[k] === 'string') clean[k] = Number(clean[k]);
  });
  if (!clean.enable_expiry_rules) {
    clean.min_guaranteed_shelf_life_days = null;
    clean.min_guaranteed_shelf_life_percent = null;
  }
  return clean;
};

const LISTING_TYPES = [
  { value: 'product', label: 'Products' },
  { value: 'service', label: 'Services' },
  { value: 'rental', label: 'Rentals' },
  { value: 'event', label: 'Events' },
  { value: 'classified', label: 'Classifieds' },
  { value: 'subscription', label: 'Subscriptions' },
  { value: 'volunteering', label: 'Volunteering' }
];

const Categories = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showBulkSubcategoryModal, setShowBulkSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [stats, setStats] = useState({
    total_categories: 0,
    active_categories: 0,
    total_subcategories: 0
  });

  // Shopify collection mapping state
  const [shopifyCollections, setShopifyCollections] = useState([]);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  // iter-344 — Custom license trust-badge image upload (Category/Subcategory forms)
  const [badgeUploading, setBadgeUploading] = useState(false);

  const uploadLicenseBadgeImage = async (file, onUploaded) => {
    setBadgeUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData,
      });
      if (response.ok) {
        const data = await response.json();
        onUploaded(data.cdn_url || data.file_url || data.url);
      } else {
        toast({ title: 'Upload failed', description: 'Could not upload the badge image. Try again.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Upload failed', description: 'Could not upload the badge image. Try again.', variant: 'destructive' });
    } finally {
      setBadgeUploading(false);
    }
  };

  // ── Auto-match helpers ─────────────────────────────────────────────────────
  const normalise = (s) =>
    (s || '').toLowerCase().trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ');

  const matchConfidence = (cmsName, shopifyTitle) => {
    const a = normalise(cmsName);
    const b = normalise(shopifyTitle);
    if (!a || !b) return { confidence: 'red', score: 0 };
    if (a === b) return { confidence: 'green', score: 1.0 };
    if (a.includes(b) || b.includes(a)) return { confidence: 'yellow', score: 0.8 };
    const wordsA = new Set(a.split(' ').filter(w => w.length > 2));
    const wordsB = new Set(b.split(' ').filter(w => w.length > 2));
    if (!wordsA.size || !wordsB.size) return { confidence: 'red', score: 0 };
    const overlap = [...wordsA].filter(w => wordsB.has(w)).length;
    const score = overlap / Math.max(wordsA.size, wordsB.size);
    if (score >= 0.6) return { confidence: 'yellow', score };
    return { confidence: 'red', score };
  };

  const autoMatch = (cmsName, collections) => {
    let best = null, bestScore = 0;
    for (const col of collections) {
      const { confidence, score } = matchConfidence(cmsName, col.title);
      if (score > bestScore) { bestScore = score; best = { ...col, confidence }; }
    }
    return bestScore >= 0.5 ? best : null;
  };

  const confidenceDot = (confidence) => {
    if (confidence === 'green')  return 'bg-green-500';
    if (confidence === 'yellow') return 'bg-yellow-400';
    return 'bg-red-400';
  };

  const getStoredConfidence = (shopifyCollectionsList) => {
    if (!shopifyCollectionsList?.length) return null;
    return shopifyCollectionsList[0].confidence || 'green';
  };

  const getPrimaryCollection = (shopifyCollectionsList) =>
    shopifyCollectionsList?.find(c => c.is_primary) || shopifyCollectionsList?.[0];

  // Fetch Shopify collections for the dropdown
  const fetchShopifyCollections = async () => {
    setCollectionsLoading(true);
    try {
      const data = await apiCall('/api/shopify-sync/collections-list');
      if (data?.connected) {
        setShopifyCollections(data.collections || []);
        setShopifyConnected(true);
      }
    } catch (_) {
      // Shopify may not be connected — that's fine
    } finally {
      setCollectionsLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '', slug: '', description: '', image: '',
    listing_types: [], is_active: true, sort_order: 0,
    hsn_code: '', sac_code: '', meta_title: '', meta_description: '',
    required_license_type: '', shopify_collections: [],
    ...COMPLIANCE_DEFAULTS,
  });

  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '', slug: '', description: '', image: '',
    is_active: true, sort_order: 0, hsn_code: '', sac_code: '',
    gst_config: { has_threshold_rates: false, default_rate: 18, slabs: [] },
    manual_pricing_enabled: false, manual_pricing_message: '',
    required_license_type: '', shopify_collections: [],
    ...COMPLIANCE_DEFAULTS,
  });

  useEffect(() => {
    fetchCategories();
    fetchStats();
    fetchShopifyCollections();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await apiCall('/api/categories/');
      setCategories(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load categories',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiCall('/api/categories/stats/summary');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = scrubCompliancePayload(formData);
      if (editingCategory) {
        await apiCall(`/api/categories/${editingCategory.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        toast({ title: 'Success', description: 'Category updated successfully' });
      } else {
        await apiCall('/api/categories/', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast({ title: 'Success', description: 'Category created successfully' });
      }
      fetchCategories();
      fetchStats();
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save category',
        variant: 'destructive'
      });
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = scrubCompliancePayload(subcategoryFormData);
      if (editingSubcategory) {
        await apiCall(`/api/categories/${selectedParentCategory.id}/subcategories/${editingSubcategory.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        toast({ title: 'Success', description: 'Subcategory updated successfully' });
      } else {
        await apiCall(`/api/categories/${selectedParentCategory.id}/subcategories`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        toast({ title: 'Success', description: 'Subcategory added successfully' });
      }
      fetchCategories();
      fetchStats();
      resetSubcategoryForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save subcategory',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    // Auto-match if no stored mapping yet
    const stored = category.shopify_collections || [];
    const collections = stored.length > 0 ? stored : (() => {
      const match = autoMatch(category.name, shopifyCollections);
      return match ? [{ collection_id: match.id, collection_title: match.title,
                        is_primary: true, confidence: match.confidence, auto_matched: true }] : [];
    })();
    setFormData({
      name: category.name, slug: category.slug,
      description: category.description || '', image: category.image || '',
      listing_types: category.listing_types || [], is_active: category.is_active,
      sort_order: category.sort_order || 0, hsn_code: category.hsn_code || '',
      sac_code: category.sac_code || '', meta_title: category.meta_title || '',
      meta_description: category.meta_description || '',
      required_license_type: category.required_license_type || '',
      shopify_collections: collections,
      license_field_label: category.license_field_label || '',
      license_badge_url: category.license_badge_url || '',
      enable_expiry_rules: category.enable_expiry_rules || false,
      expiry_date_format: category.expiry_date_format || 'shelf_life',
      expiry_legal_label: category.expiry_legal_label || 'Best Before',
      min_guaranteed_shelf_life_days: category.min_guaranteed_shelf_life_days ?? '',
      min_guaranteed_shelf_life_percent: category.min_guaranteed_shelf_life_percent ?? '',
      enable_warranty_rules: category.enable_warranty_rules || false,
      default_assurance_type: category.default_assurance_type || 'manufacturer_warranty',
      default_warranty_duration_unit: category.default_warranty_duration_unit || 'months',
      buying_mode_override_enabled: category.buying_mode_override_enabled || false,
      buying_mode_override_type: category.buying_mode_override_type || 'enquiry',
      deemed_eco_category: category.deemed_eco_category || false,
    });
    setShowModal(true);
  };

  const handleEditSubcategory = (category, subcategory) => {
    setSelectedParentCategory(category);
    setEditingSubcategory(subcategory);
    const stored = subcategory.shopify_collections || [];
    const collections = stored.length > 0 ? stored : (() => {
      const match = autoMatch(subcategory.name, shopifyCollections);
      return match ? [{ collection_id: match.id, collection_title: match.title,
                        is_primary: true, confidence: match.confidence, auto_matched: true }] : [];
    })();
    setSubcategoryFormData({
      name: subcategory.name, slug: subcategory.slug,
      description: subcategory.description || '', image: subcategory.image || '',
      is_active: subcategory.is_active, sort_order: subcategory.sort_order || 0,
      hsn_code: subcategory.hsn_code || '', sac_code: subcategory.sac_code || '',
      gst_config: subcategory.gst_config || { has_threshold_rates: false, default_rate: 18, slabs: [] },
      manual_pricing_enabled: subcategory.manual_pricing_enabled || false,
      manual_pricing_message: subcategory.manual_pricing_message || '',
      required_license_type: subcategory.required_license_type || '',
      shopify_collections: collections,
      license_field_label: subcategory.license_field_label || '',
      license_badge_url: subcategory.license_badge_url || '',
      enable_expiry_rules: subcategory.enable_expiry_rules || false,
      expiry_date_format: subcategory.expiry_date_format || 'shelf_life',
      expiry_legal_label: subcategory.expiry_legal_label || 'Best Before',
      min_guaranteed_shelf_life_days: subcategory.min_guaranteed_shelf_life_days ?? '',
      min_guaranteed_shelf_life_percent: subcategory.min_guaranteed_shelf_life_percent ?? '',
      enable_warranty_rules: subcategory.enable_warranty_rules || false,
      default_assurance_type: subcategory.default_assurance_type || 'manufacturer_warranty',
      default_warranty_duration_unit: subcategory.default_warranty_duration_unit || 'months',
      buying_mode_override_enabled: subcategory.buying_mode_override_enabled || false,
      buying_mode_override_type: subcategory.buying_mode_override_type || 'enquiry',
      deemed_eco_category: subcategory.deemed_eco_category || false,
    });
    setShowSubcategoryModal(true);
  };

  const handleAddSubcategory = (category) => {
    setSelectedParentCategory(category);
    setEditingSubcategory(null);
    setSubcategoryFormData({
      name: '',
      slug: '',
      description: '',
      image: '',
      is_active: true,
      sort_order: 0,
      hsn_code: '',
      sac_code: '',
      required_license_type: '',
      gst_config: {
        has_threshold_rates: false,
        default_rate: 18,
        slabs: []
      },
      ...COMPLIANCE_DEFAULTS,
    });
    setShowSubcategoryModal(true);
  };

  const handleBulkSubcategory = (category) => {
    setSelectedParentCategory(category);
    setShowBulkSubcategoryModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) return;
    
    try {
      await apiCall(`/api/categories/${categoryId}`, { method: 'DELETE' });
      toast({ title: 'Success', description: 'Category deleted successfully' });
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete category',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryId) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) return;
    
    try {
      await apiCall(`/api/categories/${categoryId}/subcategories/${subcategoryId}`, { method: 'DELETE' });
      toast({ title: 'Success', description: 'Subcategory deleted successfully' });
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete subcategory',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', slug: '', description: '', image: '',
      listing_types: [], is_active: true, sort_order: 0,
      hsn_code: '', sac_code: '', meta_title: '', meta_description: '',
      required_license_type: '', shopify_collections: [],
      ...COMPLIANCE_DEFAULTS,
    });
    setEditingCategory(null);
    setShowModal(false);
  };

  const resetSubcategoryForm = () => {
    setSubcategoryFormData({
      name: '', slug: '', description: '', image: '',
      is_active: true, sort_order: 0, hsn_code: '', sac_code: '',
      gst_config: { has_threshold_rates: false, default_rate: 18, slabs: [] },
      manual_pricing_enabled: false, manual_pricing_message: '',
      required_license_type: '', shopify_collections: [],
      ...COMPLIANCE_DEFAULTS,
    });
    setEditingSubcategory(null);
    setSelectedParentCategory(null);
    setShowSubcategoryModal(false);
  };

  // ── Shopify collection multi-select picker ─────────────────────────────────
  const ShopifyCollectionPicker = ({ value = [], onChange, label = "Shopify Collections" }) => {
    const [search, setSearch] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const filtered = shopifyCollections.filter(c =>
      c.title.toLowerCase().includes(search.toLowerCase())
    );
    const toggle = (col) => {
      const exists = value.find(v => v.collection_id === col.id);
      if (exists) {
        onChange(value.filter(v => v.collection_id !== col.id));
      } else {
        const { confidence } = matchConfidence(label === "Shopify Collections"
          ? formData.name : subcategoryFormData.name, col.title);
        onChange([...value, {
          collection_id: col.id, collection_title: col.title,
          is_primary: value.length === 0, confidence, auto_matched: false
        }]);
      }
    };
    const setPrimary = (colId) => {
      onChange(value.map(v => ({ ...v, is_primary: v.collection_id === colId })));
    };
    const dotColor = (conf) => conf === 'green' ? 'bg-green-500' : conf === 'yellow' ? 'bg-yellow-400' : 'bg-red-400';

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{label}</Label>
          {!shopifyConnected && (
            <span className="text-xs text-gray-400">Shopify not connected</span>
          )}
        </div>

        {/* Selected chips */}
        {value.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-2 border rounded-md bg-gray-50">
            {value.map(v => (
              <div key={v.collection_id} className="flex items-center gap-1 bg-white border rounded-full px-2.5 py-0.5 text-xs shadow-sm">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor(v.confidence)}`} />
                <span className="font-medium">{v.collection_title}</span>
                {v.is_primary && (
                  <span className="bg-blue-100 text-blue-700 rounded-full px-1.5 py-0 text-[10px]">Primary</span>
                )}
                {!v.is_primary && (
                  <button type="button" onClick={() => setPrimary(v.collection_id)}
                    className="text-gray-400 hover:text-blue-600 ml-0.5" title="Set as primary">★</button>
                )}
                <button type="button" onClick={() => toggle({ id: v.collection_id, title: v.collection_title })}
                  className="text-gray-400 hover:text-red-500 ml-0.5">×</button>
              </div>
            ))}
          </div>
        )}

        {/* Dropdown trigger */}
        {shopifyConnected && (
          <div className="relative">
            <div className="flex gap-2">
              <Input
                placeholder={collectionsLoading ? "Loading collections..." : "Search Shopify collections..."}
                value={search}
                onFocus={() => setOpen(true)}
                onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
                disabled={collectionsLoading}
                className="text-sm"
              />
              {open && <button type="button" onClick={() => setOpen(false)}
                className="text-xs text-gray-400 whitespace-nowrap">Close</button>}
            </div>
            {open && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-xs text-gray-400 p-3">No collections found</p>
                ) : filtered.map(col => {
                  const selected = value.find(v => v.collection_id === col.id);
                  const { confidence } = matchConfidence(search || '', col.title);
                  return (
                    <label key={col.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={!!selected}
                        onChange={() => { toggle(col); setSearch(''); }}
                        className="rounded" />
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor(col._confidence || 'red')}`} />
                      <span className="text-sm">{col.title}</span>
                      <span className="text-xs text-gray-400 ml-auto">{col.type}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-gray-400">
          First selection = Primary collection. Additional selections = "Also appears in".
        </p>
      </div>
    );
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleListingType = (type) => {
    setFormData(prev => ({
      ...prev,
      listing_types: prev.listing_types.includes(type)
        ? prev.listing_types.filter(t => t !== type)
        : [...prev.listing_types, type]
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Loading categories...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categories & Subcategories</h1>
            <p className="text-gray-600 mt-2">Manage product hierarchy across all listing types</p>
          </div>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>

        {/* CSV Import/Export Manager */}
        <CategoryCSVManager 
          onImportSuccess={() => {
            fetchCategories();
            fetchStats();
          }}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Categories</p>
                  <p className="text-2xl font-bold">{stats.total_categories}</p>
                </div>
                <FolderTree className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Categories</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active_categories}</p>
                </div>
                <FolderTree className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Subcategories</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.total_subcategories}</p>
                </div>
                <Tag className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Categories List - Organized by Listing Type */}
        <Card>
          <CardHeader>
            <CardTitle>All Categories by Listing Type</CardTitle>
            <CardDescription>Categories organized by listing type and sorted alphabetically</CardDescription>
          </CardHeader>
          <CardContent>
            {categories.length === 0 ? (
              <div className="text-center py-12">
                <FolderTree className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Categories Yet</h3>
                <p className="text-gray-600 mb-4">Create your first category to get started</p>
                <Button onClick={() => setShowModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Category
                </Button>
              </div>
            ) : (
              <div className="space-y-8">
                {LISTING_TYPES.map((listingType) => {
                  // Filter categories for this listing type and sort alphabetically
                  const filteredCategories = categories
                    .filter(cat => cat.listing_types.includes(listingType.value))
                    .sort((a, b) => a.name.localeCompare(b.name));
                  
                  if (filteredCategories.length === 0) return null;
                  
                  return (
                    <div key={listingType.value} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                        <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {listingType.label}
                        </div>
                        <span className="text-sm text-gray-600">
                          {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        {filteredCategories.map((category) => (
                          <div key={category.id} className="border rounded-lg bg-white">
                            <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                              <div className="flex items-center gap-3 flex-1">
                                <button
                                  onClick={() => toggleCategory(category.id)}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  {expandedCategories[category.id] ? (
                                    <ChevronDown className="h-5 w-5" />
                                  ) : (
                                    <ChevronRight className="h-5 w-5" />
                                  )}
                                </button>
                                <FolderTree className="h-5 w-5 text-blue-600" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                                    {!category.is_active && (
                                      <Badge variant="secondary">Inactive</Badge>
                                    )}
                                    {category.subcategories?.length > 0 && (
                                      <Badge variant="outline" className="text-xs">
                                        {category.subcategories.length} {category.subcategories.length === 1 ? 'subcategory' : 'subcategories'}
                                      </Badge>
                                    )}
                                  </div>
                                  {category.description && (
                                    <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                                  )}
                                  {/* Shopify collection mapping indicator */}
                                  {shopifyConnected && (() => {
                                    const primary = getPrimaryCollection(category.shopify_collections);
                                    const confidence = getStoredConfidence(category.shopify_collections);
                                    const extra = (category.shopify_collections?.length || 0) - 1;
                                    return (
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <span className="text-xs text-gray-400">Collection:</span>
                                        {primary ? (
                                          <>
                                            <span className="text-xs text-gray-700 font-medium">{primary.collection_title}</span>
                                            <span className={`w-2 h-2 rounded-full ${confidenceDot(confidence)}`} title={confidence} />
                                            {extra > 0 && <span className="text-xs text-gray-400">+{extra} more</span>}
                                          </>
                                        ) : (
                                          <>
                                            <span className="text-xs text-red-400 italic">Not linked</span>
                                            <span className="w-2 h-2 rounded-full bg-red-400" />
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAddSubcategory(category)}
                                  title="Add subcategory"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleBulkSubcategory(category)}
                                  title="Bulk add/edit subcategories"
                                >
                                  <Layers className="h-4 w-4 text-blue-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(category)}
                                  title="Edit category"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(category.id)}
                                  title="Delete category"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>

                            {expandedCategories[category.id] && category.subcategories && category.subcategories.length > 0 && (
                              <div className="border-t bg-gray-50 p-4">
                                <div className="space-y-2 ml-8">
                                  {[...category.subcategories]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((subcat) => (
                            <div
                              key={subcat.id}
                              className="flex items-center justify-between p-3 bg-white rounded border"
                            >
                              <div className="flex items-center gap-3">
                                <Tag className="h-4 w-4 text-purple-600" />
                                <div>
                                  <h4 className="font-medium">{subcat.name}</h4>
                                  {subcat.description && (
                                    <p className="text-sm text-gray-600">{subcat.description}</p>
                                  )}
                                  {/* Show GST info if configured */}
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {subcat.hsn_code && (
                                      <Badge variant="outline" className="text-xs">HSN: {subcat.hsn_code}</Badge>
                                    )}
                                    {subcat.sac_code && (
                                      <Badge variant="outline" className="text-xs">SAC: {subcat.sac_code}</Badge>
                                    )}
                                    {subcat.required_license_type && (
                                      <Badge className="bg-red-100 text-red-800 text-xs" data-testid={`subcat-license-badge-${subcat.id}`}>
                                        Requires {subcat.required_license_type}
                                      </Badge>
                                    )}
                                    {subcat.enable_expiry_rules && (
                                      <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid={`subcat-expiry-badge-${subcat.id}`}>
                                        Expiry Rules On
                                      </Badge>
                                    )}
                                    {subcat.enable_warranty_rules && (
                                      <Badge className="bg-indigo-100 text-indigo-800 text-xs" data-testid={`subcat-warranty-badge-${subcat.id}`}>
                                        Warranty Rules On
                                      </Badge>
                                    )}
                                    {subcat.buying_mode_override_enabled && (
                                      <Badge className="bg-orange-100 text-orange-800 text-xs" data-testid={`subcat-buying-mode-badge-${subcat.id}`}>
                                        Locked: {subcat.buying_mode_override_type === 'cart' ? 'Cart / Buy Now' : 'Enquiry Only'}
                                      </Badge>
                                    )}
                                    {subcat.deemed_eco_category && (
                                      <Badge className="bg-rose-100 text-rose-800 text-xs" data-testid={`subcat-deemed-eco-badge-${subcat.id}`}>
                                        Section 9(5) — GST Mandatory
                                      </Badge>
                                    )}
                                    {subcat.gst_config?.has_threshold_rates ? (
                                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                                        <Percent className="h-3 w-3 mr-1" />
                                        {subcat.gst_config.slabs?.length || 0} GST Slabs
                                      </Badge>
                                    ) : subcat.gst_config?.default_rate !== undefined ? (
                                      <Badge className="bg-green-100 text-green-800 text-xs">
                                        GST: {subcat.gst_config.default_rate}%
                                      </Badge>
                                    ) : null}
                                  </div>
                                  {/* Sub-collection indicator */}
                                  {shopifyConnected && (() => {
                                    const primary = getPrimaryCollection(subcat.shopify_collections);
                                    const conf = getStoredConfidence(subcat.shopify_collections);
                                    const extra = (subcat.shopify_collections?.length || 0) - 1;
                                    return (
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <span className="text-xs text-gray-400">Sub-collection:</span>
                                        {primary ? (
                                          <>
                                            <span className="text-xs text-gray-600">{primary.collection_title}</span>
                                            <span className={`w-2 h-2 rounded-full ${confidenceDot(conf)}`} title={conf} />
                                            {extra > 0 && <span className="text-xs text-gray-400">+{extra}</span>}
                                          </>
                                        ) : (
                                          <>
                                            <span className="text-xs text-red-400 italic">Not linked</span>
                                            <span className="w-2 h-2 rounded-full bg-red-400" />
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                                {!subcat.is_active && (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditSubcategory(category, subcat)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteSubcategory(category.id, subcat.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {editingCategory ? 'Edit Category' : 'Create New Category'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Category Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          if (!editingCategory) {
                            setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }));
                          }
                        }}
                        placeholder="e.g., Electronics"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="slug">Slug *</Label>
                      <Input
                        id="slug"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        placeholder="e.g., electronics"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of this category"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Listing Types *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {LISTING_TYPES.map(type => (
                        <div key={type.value} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={type.value}
                            checked={formData.listing_types.includes(type.value)}
                            onChange={() => toggleListingType(type.value)}
                            className="rounded"
                          />
                          <Label htmlFor={type.value} className="font-normal cursor-pointer">
                            {type.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sort_order">Sort Order</Label>
                      <Input
                        id="sort_order"
                        type="number"
                        value={formData.sort_order}
                        onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-8">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="rounded"
                      />
                      <Label htmlFor="is_active" className="font-normal cursor-pointer">Active</Label>
                    </div>
                  </div>

                  {/* HSN/SAC Codes */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Tax Classification Codes</h4>
                    <p className="text-sm text-gray-500 mb-3">
                      These codes will be auto-suggested when vendors create listings in this category
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="hsn_code">HSN Code (Products)</Label>
                        <Input
                          id="hsn_code"
                          value={formData.hsn_code}
                          onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })}
                          placeholder="e.g., 84713010"
                          maxLength={8}
                        />
                        <p className="text-xs text-gray-400 mt-1">6-8 digit code for goods</p>
                      </div>
                      <div>
                        <Label htmlFor="sac_code">SAC Code (Services)</Label>
                        <Input
                          id="sac_code"
                          value={formData.sac_code}
                          onChange={(e) => setFormData({ ...formData, sac_code: e.target.value })}
                          placeholder="e.g., 998314"
                          maxLength={6}
                        />
                        <p className="text-xs text-gray-400 mt-1">6-digit code starting with 99</p>
                      </div>
                    </div>
                  </div>

                  {/* iter-332 — Compliance & Regulatory Rules (License / Shelf-Life / Warranty / Return Window) */}
                  <div className="border-t pt-4 mt-4 space-y-5" data-testid="category-compliance-card">
                    <h4 className="font-medium text-gray-900">Compliance &amp; Regulatory Rules</h4>

                    {/* 1. License Group */}
                    <div className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="cat_license_toggle" className="cursor-pointer">Require Mandatory Vendor License</Label>
                        <Switch
                          id="cat_license_toggle"
                          data-testid="category-license-toggle"
                          checked={!!formData.required_license_type}
                          onCheckedChange={(checked) => setFormData({ ...formData, required_license_type: checked ? 'FSSAI' : '' })}
                        />
                      </div>
                      {formData.required_license_type && (
                        <>
                          <div>
                            <Label htmlFor="required_license_type">Select License Type</Label>
                            <select
                              id="required_license_type"
                              data-testid="category-required-license-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={formData.required_license_type}
                              onChange={(e) => setFormData({ ...formData, required_license_type: e.target.value })}
                            >
                              <option value="FSSAI">FSSAI License</option>
                              <option value="BIS">BIS / ISI Registration</option>
                              <option value="CDSCO">CDSCO / State Drug License</option>
                              <option value="AGMARK">AGMARK Certification</option>
                              <option value="ISI">ISI Mark Registration</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="cat_license_field_label">Frontend Field Label</Label>
                            <Input
                              id="cat_license_field_label"
                              data-testid="category-license-label-input"
                              value={formData.license_field_label}
                              onChange={(e) => setFormData({ ...formData, license_field_label: e.target.value })}
                              placeholder="e.g., FSSAI License No."
                            />
                          </div>
                          <div>
                            <Label>Trust Badge Image</Label>
                            <p className="text-xs text-gray-400 mb-1">Leave blank to use the default {formData.required_license_type} mark. Upload to override.</p>
                            <div className="flex items-center gap-2">
                              {formData.license_badge_url && (
                                <img src={formData.license_badge_url} alt="badge" className="w-10 h-10 rounded-lg border object-contain" data-testid="category-license-badge-preview" />
                              )}
                              <label className="text-sm text-indigo-600 font-medium cursor-pointer" data-testid="category-license-badge-upload-btn">
                                {badgeUploading ? 'Uploading...' : (formData.license_badge_url ? 'Replace Image' : 'Upload Custom Badge')}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={badgeUploading}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) uploadLicenseBadgeImage(file, (url) => setFormData(prev => ({ ...prev, license_badge_url: url })));
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                              {formData.license_badge_url && (
                                <button type="button" className="text-xs text-red-500" onClick={() => setFormData(prev => ({ ...prev, license_badge_url: '' }))} data-testid="category-license-badge-remove-btn">
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      <p className="text-xs text-gray-400">
                        When on, vendors MUST enter a matching license number to publish listings in this category
                        (unless a subcategory below overrides it).
                      </p>
                    </div>

                    {/* 2. Shelf-Life / Expiry Group */}
                    <div className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="cat_expiry_toggle" className="cursor-pointer">Enable Perishable / Expiry Rules</Label>
                        <Switch
                          id="cat_expiry_toggle"
                          data-testid="category-expiry-toggle"
                          checked={formData.enable_expiry_rules}
                          onCheckedChange={(checked) => setFormData({ ...formData, enable_expiry_rules: checked })}
                        />
                      </div>
                      {formData.enable_expiry_rules && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="cat_expiry_format">Frontend Date Format</Label>
                            <select
                              id="cat_expiry_format"
                              data-testid="category-expiry-format-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={formData.expiry_date_format}
                              onChange={(e) => setFormData({ ...formData, expiry_date_format: e.target.value })}
                            >
                              <option value="shelf_life">Show Total Shelf Life</option>
                              <option value="exact_date">Show Exact Expiry Date</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="cat_expiry_label">Legal Label Wording</Label>
                            <select
                              id="cat_expiry_label"
                              data-testid="category-expiry-label-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={formData.expiry_legal_label}
                              onChange={(e) => setFormData({ ...formData, expiry_legal_label: e.target.value })}
                            >
                              <option value="Best Before">Best Before</option>
                              <option value="Use By">Use By</option>
                              <option value="Expiration Date">Expiration Date</option>
                              <option value="Shelf Life">Shelf Life</option>
                            </select>
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label>Minimum Guaranteed Shelf Life at Delivery</Label>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-gray-500 font-normal">Fixed Days Floor</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  data-testid="category-min-shelf-life-input"
                                  value={formData.min_guaranteed_shelf_life_days}
                                  onChange={(e) => setFormData({ ...formData, min_guaranteed_shelf_life_days: e.target.value })}
                                  placeholder="e.g., 45"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-normal">% of Total Shelf Life Floor</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="100"
                                  data-testid="category-min-shelf-life-percent-input"
                                  value={formData.min_guaranteed_shelf_life_percent}
                                  onChange={(e) => setFormData({ ...formData, min_guaranteed_shelf_life_percent: e.target.value })}
                                  placeholder="e.g., 70 (%)"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-400">Both are required when enabled. The effective floor for each product is whichever value is greater.</p>
                            {(formData.min_guaranteed_shelf_life_days && formData.min_guaranteed_shelf_life_percent) && (
                              <p className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="category-shelf-life-guarantee-preview">
                                <Shield size={14} className="shrink-0" />
                                Minimum Guaranteed Shelf Life at delivery: at least {formData.min_guaranteed_shelf_life_days} days OR {formData.min_guaranteed_shelf_life_percent}% of the Total Shelf Life, whichever is greater
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 3. Warranty / Guarantee Group */}
                    <div className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="cat_warranty_toggle" className="cursor-pointer">Enable Warranty / Guarantee Fields</Label>
                        <Switch
                          id="cat_warranty_toggle"
                          data-testid="category-warranty-toggle"
                          checked={formData.enable_warranty_rules}
                          onCheckedChange={(checked) => setFormData({ ...formData, enable_warranty_rules: checked })}
                        />
                      </div>
                      {formData.enable_warranty_rules && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="cat_assurance_type">Assurance Type</Label>
                            <select
                              id="cat_assurance_type"
                              data-testid="category-assurance-type-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={formData.default_assurance_type}
                              onChange={(e) => setFormData({ ...formData, default_assurance_type: e.target.value })}
                            >
                              <option value="manufacturer_warranty">Manufacturer Warranty</option>
                              <option value="seller_warranty">Seller Warranty</option>
                              <option value="brand_guarantee">Brand Guarantee</option>
                              <option value="none">None</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="cat_duration_unit">Default Duration Unit</Label>
                            <select
                              id="cat_duration_unit"
                              data-testid="category-duration-unit-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={formData.default_warranty_duration_unit}
                              onChange={(e) => setFormData({ ...formData, default_warranty_duration_unit: e.target.value })}
                            >
                              <option value="months">Months</option>
                              <option value="years">Years</option>
                            </select>
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-gray-400">
                        Vendor must select a duration when publishing in this category.
                      </p>
                    </div>

                    {/* 4. Buying Mode Override (temporary compliance hold) */}
                    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="cat_buying_mode_toggle" className="cursor-pointer">Force Buying Mode (Compliance Hold)</Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            When ON, every listing in this category shows only the selected buying mode's CTA — overriding each
                            vendor's own selection. Turning it OFF instantly restores every vendor's original choice.
                          </p>
                        </div>
                        <Switch
                          id="cat_buying_mode_toggle"
                          data-testid="category-buying-mode-override-toggle"
                          checked={formData.buying_mode_override_enabled}
                          onCheckedChange={(checked) => setFormData({ ...formData, buying_mode_override_enabled: checked })}
                        />
                      </div>
                      {formData.buying_mode_override_enabled && (
                        <div>
                          <Label htmlFor="cat_buying_mode_type">Forced Buying Mode</Label>
                          <select
                            id="cat_buying_mode_type"
                            data-testid="category-buying-mode-type-select"
                            className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                            value={formData.buying_mode_override_type}
                            onChange={(e) => setFormData({ ...formData, buying_mode_override_type: e.target.value })}
                          >
                            <option value="enquiry">Enquiry Only (WhatsApp)</option>
                            <option value="cart">Cart / Buy Now</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* iter-9(5) — Section 9(5) Deemed ECO */}
                    <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="cat_deemed_eco_toggle" className="cursor-pointer">Deemed ECO — Section 9(5) (Transport / Hospitality)</Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            For passenger transport, housekeeping, hotels or standalone restaurants. When ON, only
                            vendors with full GST (Tier 3/4) get Cart / Buy Now here — Tier 1 AND Tier 2 vendors
                            are auto-degraded to Enquiry Only, since Tier 2's EID/composition path does not satisfy
                            Section 9(5)'s mandatory-GST rule.
                          </p>
                        </div>
                        <Switch
                          id="cat_deemed_eco_toggle"
                          data-testid="category-deemed-eco-toggle"
                          checked={formData.deemed_eco_category}
                          onCheckedChange={(checked) => setFormData({ ...formData, deemed_eco_category: checked })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Shopify Collection Mapping */}
                  {shopifyConnected && (
                    <div className="border-t pt-4 mt-2">
                      <ShopifyCollectionPicker
                        label="Shopify Collections"
                        value={formData.shopify_collections}
                        onChange={(v) => setFormData({ ...formData, shopify_collections: v })}
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                    <Button type="submit">{editingCategory ? 'Update Category' : 'Create Category'}</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Subcategory Modal */}
        {showSubcategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col">
              <div className="p-6 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">
                    {editingSubcategory ? 'Edit Subcategory' : `Add Subcategory to ${selectedParentCategory?.name}`}
                  </h3>
                  <Button variant="ghost" size="icon" onClick={resetSubcategoryForm}>
                    <span className="text-2xl">&times;</span>
                  </Button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <form onSubmit={handleSubcategorySubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sub_name">Subcategory Name *</Label>
                      <Input
                        id="sub_name"
                        value={subcategoryFormData.name}
                        onChange={(e) => {
                          setSubcategoryFormData({ ...subcategoryFormData, name: e.target.value });
                          if (!editingSubcategory) {
                            setSubcategoryFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }));
                          }
                        }}
                        placeholder="e.g., Laptops"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="sub_slug">Slug *</Label>
                      <Input
                        id="sub_slug"
                        value={subcategoryFormData.slug}
                        onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, slug: e.target.value })}
                        placeholder="e.g., laptops"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sub_description">Description</Label>
                    <Textarea
                      id="sub_description"
                      value={subcategoryFormData.description}
                      onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, description: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sub_sort_order">Sort Order</Label>
                      <Input
                        id="sub_sort_order"
                        type="number"
                        value={subcategoryFormData.sort_order}
                        onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, sort_order: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-8">
                      <input
                        type="checkbox"
                        id="sub_is_active"
                        checked={subcategoryFormData.is_active}
                        onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, is_active: e.target.checked })}
                        className="rounded"
                      />
                      <Label htmlFor="sub_is_active" className="font-normal cursor-pointer">Active</Label>
                    </div>
                  </div>

                  {/* HSN/SAC Codes for Subcategory */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Tax Classification Codes</h4>
                    <p className="text-sm text-gray-500 mb-3">
                      Override parent category codes for this subcategory
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="sub_hsn_code">HSN Code (Products)</Label>
                        <Input
                          id="sub_hsn_code"
                          value={subcategoryFormData.hsn_code}
                          onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, hsn_code: e.target.value })}
                          placeholder="e.g., 84713010"
                          maxLength={8}
                        />
                        <p className="text-xs text-gray-400 mt-1">6-8 digit code for goods</p>
                      </div>
                      <div>
                        <Label htmlFor="sub_sac_code">SAC Code (Services)</Label>
                        <Input
                          id="sub_sac_code"
                          value={subcategoryFormData.sac_code}
                          onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, sac_code: e.target.value })}
                          placeholder="e.g., 998314"
                          maxLength={6}
                        />
                        <p className="text-xs text-gray-400 mt-1">6-digit code starting with 99</p>
                      </div>
                    </div>
                  </div>

                  {/* iter-332 — Compliance & Regulatory Rules override (subcategory) */}
                  <div className="border-t pt-4 mt-4 space-y-5" data-testid="subcategory-compliance-card">
                    <h4 className="font-medium text-gray-900">Compliance &amp; Regulatory Rules</h4>
                    <p className="text-xs text-gray-500">Leave a toggle off to inherit the parent category's rule.</p>

                    <div className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sub_license_toggle" className="cursor-pointer">Require Mandatory Vendor License</Label>
                        <Switch
                          id="sub_license_toggle"
                          data-testid="subcategory-license-toggle"
                          checked={!!subcategoryFormData.required_license_type}
                          onCheckedChange={(checked) => setSubcategoryFormData({ ...subcategoryFormData, required_license_type: checked ? 'FSSAI' : '' })}
                        />
                      </div>
                      {subcategoryFormData.required_license_type && (
                        <>
                          <div>
                            <Label htmlFor="sub_required_license_type">Select License Type</Label>
                            <select
                              id="sub_required_license_type"
                              data-testid="subcategory-required-license-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={subcategoryFormData.required_license_type}
                              onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, required_license_type: e.target.value })}
                            >
                              <option value="FSSAI">FSSAI License</option>
                              <option value="BIS">BIS / ISI Registration</option>
                              <option value="CDSCO">CDSCO / State Drug License</option>
                              <option value="AGMARK">AGMARK Certification</option>
                              <option value="ISI">ISI Mark Registration</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="sub_license_field_label">Frontend Field Label</Label>
                            <Input
                              id="sub_license_field_label"
                              data-testid="subcategory-license-label-input"
                              value={subcategoryFormData.license_field_label}
                              onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, license_field_label: e.target.value })}
                              placeholder="e.g., FSSAI License No."
                            />
                          </div>
                          <div>
                            <Label>Trust Badge Image</Label>
                            <p className="text-xs text-gray-400 mb-1">Leave blank to inherit the category's badge.</p>
                            <div className="flex items-center gap-2">
                              {subcategoryFormData.license_badge_url && (
                                <img src={subcategoryFormData.license_badge_url} alt="badge" className="w-10 h-10 rounded-lg border object-contain" data-testid="subcategory-license-badge-preview" />
                              )}
                              <label className="text-sm text-indigo-600 font-medium cursor-pointer" data-testid="subcategory-license-badge-upload-btn">
                                {badgeUploading ? 'Uploading...' : (subcategoryFormData.license_badge_url ? 'Replace Image' : 'Upload Custom Badge')}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={badgeUploading}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) uploadLicenseBadgeImage(file, (url) => setSubcategoryFormData(prev => ({ ...prev, license_badge_url: url })));
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                              {subcategoryFormData.license_badge_url && (
                                <button type="button" className="text-xs text-red-500" onClick={() => setSubcategoryFormData(prev => ({ ...prev, license_badge_url: '' }))} data-testid="subcategory-license-badge-remove-btn">
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sub_expiry_toggle" className="cursor-pointer">Enable Perishable / Expiry Rules</Label>
                        <Switch
                          id="sub_expiry_toggle"
                          data-testid="subcategory-expiry-toggle"
                          checked={subcategoryFormData.enable_expiry_rules}
                          onCheckedChange={(checked) => setSubcategoryFormData({ ...subcategoryFormData, enable_expiry_rules: checked })}
                        />
                      </div>
                      {subcategoryFormData.enable_expiry_rules && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="sub_expiry_format">Frontend Date Format</Label>
                            <select
                              id="sub_expiry_format"
                              data-testid="subcategory-expiry-format-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={subcategoryFormData.expiry_date_format}
                              onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, expiry_date_format: e.target.value })}
                            >
                              <option value="shelf_life">Show Total Shelf Life</option>
                              <option value="exact_date">Show Exact Expiry Date</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="sub_expiry_label">Legal Label Wording</Label>
                            <select
                              id="sub_expiry_label"
                              data-testid="subcategory-expiry-label-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={subcategoryFormData.expiry_legal_label}
                              onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, expiry_legal_label: e.target.value })}
                            >
                              <option value="Best Before">Best Before</option>
                              <option value="Use By">Use By</option>
                              <option value="Expiration Date">Expiration Date</option>
                              <option value="Shelf Life">Shelf Life</option>
                            </select>
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label>Minimum Guaranteed Shelf Life at Delivery</Label>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-gray-500 font-normal">Fixed Days Floor</Label>
                                <Input
                                  id="sub_min_shelf_life"
                                  type="number"
                                  min="1"
                                  data-testid="subcategory-min-shelf-life-input"
                                  value={subcategoryFormData.min_guaranteed_shelf_life_days}
                                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, min_guaranteed_shelf_life_days: e.target.value })}
                                  placeholder="e.g., 45"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-normal">% of Total Shelf Life Floor</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="100"
                                  data-testid="subcategory-min-shelf-life-percent-input"
                                  value={subcategoryFormData.min_guaranteed_shelf_life_percent}
                                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, min_guaranteed_shelf_life_percent: e.target.value })}
                                  placeholder="e.g., 70 (%)"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-400">Both are required when enabled. The effective floor for each product is whichever value is greater.</p>
                            {(subcategoryFormData.min_guaranteed_shelf_life_days && subcategoryFormData.min_guaranteed_shelf_life_percent) && (
                              <p className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="subcategory-shelf-life-guarantee-preview">
                                <Shield size={14} className="shrink-0" />
                                Minimum Guaranteed Shelf Life at delivery: at least {subcategoryFormData.min_guaranteed_shelf_life_days} days OR {subcategoryFormData.min_guaranteed_shelf_life_percent}% of the Total Shelf Life, whichever is greater
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="sub_warranty_toggle" className="cursor-pointer">Enable Warranty / Guarantee Fields</Label>
                        <Switch
                          id="sub_warranty_toggle"
                          data-testid="subcategory-warranty-toggle"
                          checked={subcategoryFormData.enable_warranty_rules}
                          onCheckedChange={(checked) => setSubcategoryFormData({ ...subcategoryFormData, enable_warranty_rules: checked })}
                        />
                      </div>
                      {subcategoryFormData.enable_warranty_rules && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="sub_assurance_type">Assurance Type</Label>
                            <select
                              id="sub_assurance_type"
                              data-testid="subcategory-assurance-type-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={subcategoryFormData.default_assurance_type}
                              onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, default_assurance_type: e.target.value })}
                            >
                              <option value="manufacturer_warranty">Manufacturer Warranty</option>
                              <option value="seller_warranty">Seller Warranty</option>
                              <option value="brand_guarantee">Brand Guarantee</option>
                              <option value="none">None</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="sub_duration_unit">Default Duration Unit</Label>
                            <select
                              id="sub_duration_unit"
                              data-testid="subcategory-duration-unit-select"
                              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                              value={subcategoryFormData.default_warranty_duration_unit}
                              onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, default_warranty_duration_unit: e.target.value })}
                            >
                              <option value="months">Months</option>
                              <option value="years">Years</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Buying Mode Override (temporary compliance hold) */}
                    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="sub_buying_mode_toggle" className="cursor-pointer">Force Buying Mode (Compliance Hold)</Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            When ON, every listing in this subcategory shows only the selected buying mode's CTA — overriding
                            each vendor's own selection. Turning it OFF instantly restores every vendor's original choice.
                          </p>
                        </div>
                        <Switch
                          id="sub_buying_mode_toggle"
                          data-testid="subcategory-buying-mode-override-toggle"
                          checked={subcategoryFormData.buying_mode_override_enabled}
                          onCheckedChange={(checked) => setSubcategoryFormData({ ...subcategoryFormData, buying_mode_override_enabled: checked })}
                        />
                      </div>
                      {subcategoryFormData.buying_mode_override_enabled && (
                        <div>
                          <Label htmlFor="sub_buying_mode_type">Forced Buying Mode</Label>
                          <select
                            id="sub_buying_mode_type"
                            data-testid="subcategory-buying-mode-type-select"
                            className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1"
                            value={subcategoryFormData.buying_mode_override_type}
                            onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, buying_mode_override_type: e.target.value })}
                          >
                            <option value="enquiry">Enquiry Only (WhatsApp)</option>
                            <option value="cart">Cart / Buy Now</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* iter-9(5) — Section 9(5) Deemed ECO */}
                    <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="sub_deemed_eco_toggle" className="cursor-pointer">Deemed ECO — Section 9(5) (Transport / Hospitality)</Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            For passenger transport, housekeeping, hotels or standalone restaurants. When ON, only
                            vendors with full GST (Tier 3/4) get Cart / Buy Now here — Tier 1 AND Tier 2 vendors
                            are auto-degraded to Enquiry Only, since Tier 2's EID/composition path does not satisfy
                            Section 9(5)'s mandatory-GST rule.
                          </p>
                        </div>
                        <Switch
                          id="sub_deemed_eco_toggle"
                          data-testid="subcategory-deemed-eco-toggle"
                          checked={subcategoryFormData.deemed_eco_category}
                          onCheckedChange={(checked) => setSubcategoryFormData({ ...subcategoryFormData, deemed_eco_category: checked })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* GST Rate Configuration */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Percent className="h-4 w-4 text-gray-500" />
                      <h4 className="font-medium text-gray-900">GST Rate Configuration</h4>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Configure GST rates for this subcategory. Enable threshold rates for price-based slabs (e.g., hotel rooms).
                    </p>
                    <GSTRateConfig
                      value={subcategoryFormData.gst_config}
                      onChange={(config) => setSubcategoryFormData({ ...subcategoryFormData, gst_config: config })}
                    />
                  </div>

                  {/* Manual Pricing Configuration for Subcategory */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Manual Pricing Mode
                        </h4>
                        <p className="text-sm text-gray-500">
                          Enable for services where pricing depends on customization (e.g., Catering based on guest count)
                        </p>
                      </div>
                      <Switch
                        checked={subcategoryFormData.manual_pricing_enabled}
                        onCheckedChange={(checked) => setSubcategoryFormData({ ...subcategoryFormData, manual_pricing_enabled: checked })}
                      />
                    </div>
                    {subcategoryFormData.manual_pricing_enabled && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <Label htmlFor="sub_manual_pricing_message" className="text-sm text-amber-800">
                          Custom Message for Customers
                        </Label>
                        <Textarea
                          id="sub_manual_pricing_message"
                          value={subcategoryFormData.manual_pricing_message}
                          onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, manual_pricing_message: e.target.value })}
                          placeholder="e.g., Final pricing will be confirmed based on your guest count and menu selections."
                          rows={2}
                          className="mt-2"
                        />
                        <p className="text-xs text-amber-700 mt-1">
                          This overrides the parent category's message for products in this subcategory
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Shopify Sub-Collection Mapping */}
                  {shopifyConnected && (
                    <div className="border-t pt-4 mt-2">
                      <ShopifyCollectionPicker
                        label="Shopify Sub-Collections"
                        value={subcategoryFormData.shopify_collections}
                        onChange={(v) => setSubcategoryFormData({ ...subcategoryFormData, shopify_collections: v })}
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4 mt-4 border-t sticky bottom-0 bg-white pb-2">
                    <Button type="button" variant="outline" onClick={resetSubcategoryForm}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingSubcategory ? 'Update Subcategory' : 'Add Subcategory'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Subcategory Modal */}
        <BulkSubcategoryModal
          isOpen={showBulkSubcategoryModal}
          onClose={() => {
            setShowBulkSubcategoryModal(false);
            setSelectedParentCategory(null);
          }}
          category={selectedParentCategory}
          onSuccess={() => {
            fetchCategories();
            fetchStats();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default Categories;
